import "./styles.css";

import { api, setUnauthorizedHandler } from "./api";
import { loadConfig, onConfigChange, setConfig } from "./catalog";
import {
  getProject,
  loadState,
  migrateImport,
  onChange,
  onSaveStatus,
  saveNow,
  unload,
  type SaveStatus,
} from "./state";
import { initView, setImage, fitView, updateScaleBar } from "./view";
import { initOverlay, renderOverlay } from "./overlay";
import { initInteraction, onHandleDown, onMarkerDown, onShapeDown } from "./interaction";
import { onSession, resetTransient, session, setMode, setReadOnly } from "./session";
import { initSidebar, renderSidebar } from "./ui/sidebar";
import { initToolbar, renderToolbar } from "./ui/toolbar";
import { initPrint, refreshPrintExtraOptions } from "./print";
import { initProjects, renderProjectList } from "./ui/projects";
import { renderSettingsInto, openSettingsDialog } from "./ui/settings";
import { initToast, toast } from "./ui/toast";
import { confirmDialog, textPrompt } from "./ui/prompt";
import type { AuthUser } from "./types";

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} fehlt im DOM`);
  return el as T;
};

const screenAuth = $("screen-auth");
const screenProjects = $("screen-projects");
const screenPlanner = $("screen-planner");

let plannerWired = false;
let bootedAuthed = false;
let currentProjectId: string | null = null;
let currentUser: AuthUser = { id: "", username: "" };

/* ------------------------------- auth ------------------------------- */

function showScreen(which: "auth" | "app"): void {
  screenAuth.hidden = which !== "auth";
  if (which === "auth") {
    screenProjects.hidden = true;
    screenPlanner.hidden = true;
  }
}

function showAuth(needsSetup: boolean): void {
  showScreen("auth");
  $("authTitle").textContent = needsSetup ? "Ersteinrichtung" : "Anmelden";
  $("authSub").textContent = needsSetup
    ? "Lege den ersten Benutzer an. Er hat vollen Zugriff."
    : "Bitte mit Benutzername und Passwort anmelden.";
  ($("authSubmit") as HTMLButtonElement).textContent = needsSetup ? "Benutzer anlegen" : "Anmelden";
  ($("authPass") as HTMLInputElement).autocomplete = needsSetup ? "new-password" : "current-password";
  const err = $("authError");
  err.hidden = true;

  const form = $("authForm") as HTMLFormElement;
  form.onsubmit = async (ev) => {
    ev.preventDefault();
    const username = ($("authUser") as HTMLInputElement).value.trim();
    const password = ($("authPass") as HTMLInputElement).value;
    err.hidden = true;
    try {
      const user = needsSetup
        ? await api.setup(username, password)
        : await api.login(username, password);
      ($("authPass") as HTMLInputElement).value = "";
      await bootAuthed(user);
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Anmeldung fehlgeschlagen.";
      err.hidden = false;
    }
  };
}

/* ------------------------------ routing ------------------------------ */

function parseRoute(): { name: "projects" } | { name: "planner"; id: string } {
  const m = /^\/p\/([^/]+)\/?$/.exec(location.pathname);
  if (m) return { name: "planner", id: decodeURIComponent(m[1]!) };
  const h = /^#\/p\/([^/]+)$/.exec(location.hash);
  if (h) return { name: "planner", id: decodeURIComponent(h[1]!) };
  return { name: "projects" };
}

function navigateTo(path: string): void {
  if (location.pathname + location.hash !== path) history.pushState({}, "", path);
  void route();
}

async function route(): Promise<void> {
  if (!bootedAuthed) return;
  const r = parseRoute();
  if (r.name === "planner") await openPlanner(r.id);
  else showProjects();
}

function showProjects(): void {
  currentProjectId = null;
  unload();
  screenAuth.hidden = true;
  screenPlanner.hidden = true;
  screenProjects.hidden = false;
  void renderProjectList();
}

async function openPlanner(id: string): Promise<void> {
  let state;
  try {
    state = await api.getState(id);
  } catch (err) {
    toast(`Projekt konnte nicht geladen werden: ${err instanceof Error ? err.message : err}`);
    navigateTo("/");
    return;
  }

  screenAuth.hidden = true;
  screenProjects.hidden = true;
  screenPlanner.hidden = false;
  currentProjectId = id;

  if (!plannerWired) {
    wirePlanner();
    plannerWired = true;
  }

  mountPlannerState(state);
}

function mountPlannerState(state: Parameters<typeof loadState>[0]): void {
  resetTransient();
  loadState(state);
  setMode("select");
  updatePlannerHeader();

  const p = getProject();
  setImage(p.planUrl ?? "", p.imgWidth, p.imgHeight);
  renderAll();

  const img = $("planImg") as HTMLImageElement;
  img.decode().then(() => fitView()).catch(() => fitView());
}

/** Public read-only view (/share/:token) — no login, no project list, no writes. */
async function bootShareView(token: string): Promise<void> {
  let data;
  try {
    data = await api.getPublicShare(token);
  } catch {
    document.body.innerHTML =
      '<div style="display:flex; align-items:center; justify-content:center; height:100vh; font:15px var(--font-ui); color:var(--ink-600);">Dieser Link ist ungültig oder wurde beendet.</div>';
    return;
  }

  setReadOnly(true);
  document.body.setAttribute("data-readonly", "");
  setConfig(data.config);

  screenAuth.hidden = true;
  screenProjects.hidden = true;
  screenPlanner.hidden = false;
  currentProjectId = null;

  if (!plannerWired) {
    wirePlanner();
    plannerWired = true;
  }

  mountPlannerState(data);
}

function updatePlannerHeader(): void {
  const p = getProject();
  $("plannerName").textContent = p.name;
  const loc = [p.buildingName, p.floorName].filter(Boolean).join(" · ");
  $("plannerSub").textContent =
    (loc ? `${loc} · ` : "") +
    `${p.imgWidth}×${p.imgHeight} px` +
    (p.scaleRatio ? ` · Maßstab ${p.scaleRatio}` : "");
}

/* ---------------------------- render pipeline ---------------------------- */

function renderAll(): void {
  renderOverlay();
  renderSidebar();
  renderToolbar();
  updateScaleBar();
}

/* ---------------------------- planner wiring ---------------------------- */

function wirePlanner(): void {
  const viewport = $("viewport");
  const overlay = $("overlay") as unknown as SVGSVGElement;

  initView({
    viewport,
    world: $("world"),
    planImg: $("planImg") as HTMLImageElement,
    overlay,
    zoomPct: $("zoomPct"),
    scaleLabel: $("scaleLabel"),
    scaleBarInner: $("scaleBarInner"),
  });
  initOverlay(overlay, $("emptyHint"), { onMarkerDown, onShapeDown, onHandleDown });
  initInteraction(viewport, overlay);
  initSidebar({
    palette: $("palette"),
    objList: $("objList"),
    shapeList: $("shapeList"),
    shapeCatsWrap: $("shapeCats"),
    closedRow: $("closedRow"),
    lineClosed: $("lineClosed") as HTMLInputElement,
    multiPlace: $("multiPlace") as HTMLInputElement,
    btnDrawShape: $("btnDrawShape") as HTMLButtonElement,
    placeHint: $("placeHint"),
    shapeHint: $("shapeHint"),
  });
  initToolbar({
    viewSeg: $("viewSeg"),
    tensatorDone: $("btnTensatorDone") as HTMLButtonElement,
    layerToggles: $("layerToggles"),
    modeHint: $("modeHint"),
    zoomIn: $("btnZoomIn") as HTMLButtonElement,
    zoomOut: $("btnZoomOut") as HTMLButtonElement,
    fit: $("btnFit2") as HTMLButtonElement,
    viewport,
  });
  initPrint($("printSheet"), $("btnPrintGo") as HTMLButtonElement, $("printMenu"));

  wireHeader();
  wireTabs();

  onChange(renderAll);
  onSession(renderAll);
  onSession(() => {
    // The add/tool popover floats over the canvas — once a drawing/placement
    // mode is active the user needs to click the plan itself, so close it.
    if (session().mode !== "select") $("addPopover").classList.remove("open");
  });
  onSaveStatus(renderSaveStatus);
  onConfigChange(() => {
    if (currentProjectId) renderAll();
  });

  window.addEventListener("resize", () => fitView());
}

function renderSaveStatus(status: SaveStatus, detail: string): void {
  const dot = $("saveDot");
  dot.className = "save-dot" + (status === "idle" ? "" : ` ${status === "error" ? "err" : status}`);
  $("saveStatusText").textContent = detail;
}

function wireHeader(): void {
  $("btnBack").addEventListener("click", () => navigateTo("/"));
  $("btnSaveNow").addEventListener("click", () => void saveNow());

  const toggleMenu = (id: string) => {
    document.querySelectorAll(".menu").forEach((m) => {
      if (m.id !== id) m.classList.remove("open");
    });
    $(id).classList.toggle("open");
  };
  $("btnMenu").addEventListener("click", (e) => {
    e.stopPropagation();
    toggleMenu("moreMenu");
  });
  $("btnPrint").addEventListener("click", (e) => {
    e.stopPropagation();
    refreshPrintExtraOptions();
    toggleMenu("printMenu");
  });
  $("btnAddToggle").addEventListener("click", (e) => {
    e.stopPropagation();
    toggleMenu("addPopover");
  });
  document.addEventListener("click", () => {
    document.querySelectorAll(".menu").forEach((m) => m.classList.remove("open"));
  });
  document.querySelectorAll(".menu").forEach((m) => {
    m.addEventListener("click", (e) => e.stopPropagation());
  });

  $("btnExport").addEventListener("click", () => {
    if (!currentProjectId) return;
    const a = document.createElement("a");
    a.href = api.exportUrl(currentProjectId);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  });
  $("btnImportTrigger").addEventListener("click", () => ($("importFile") as HTMLInputElement).click());
  ($("importFile") as HTMLInputElement).addEventListener("change", onImportFile);

  $("btnFit").addEventListener("click", () => fitView());
  $("btnRename").addEventListener("click", onRename);
  $("btnAssignBuilding").addEventListener("click", onAssignBuilding);
  $("btnDeleteProject").addEventListener("click", onDeleteProject);
  $("btnShare").addEventListener("click", onOpenShareDialog);
  wireShareDialog();
}

function shareUrlFor(token: string): string {
  return `${location.origin}/share/${token}`;
}

function onOpenShareDialog(): void {
  const dlg = $("shareDialog") as HTMLDialogElement;
  const token = getProject().shareToken;
  $("shareOff").hidden = !!token;
  $("shareOn").hidden = !token;
  if (token) ($("shareUrl") as HTMLInputElement).value = shareUrlFor(token);
  dlg.showModal();
}

function wireShareDialog(): void {
  const dlg = $("shareDialog") as HTMLDialogElement;
  dlg.querySelector('[data-a="close"]')!.addEventListener("click", () => dlg.close());

  $("btnShareEnable").addEventListener("click", async () => {
    if (!currentProjectId) return;
    try {
      const project = await api.createShareLink(currentProjectId);
      getProject().shareToken = project.shareToken;
      $("shareOff").hidden = true;
      $("shareOn").hidden = false;
      ($("shareUrl") as HTMLInputElement).value = shareUrlFor(project.shareToken!);
    } catch (err) {
      toast(`Freigabe fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
    }
  });

  $("btnShareCopy").addEventListener("click", async () => {
    const input = $("shareUrl") as HTMLInputElement;
    try {
      await navigator.clipboard.writeText(input.value);
      toast("Link kopiert.");
    } catch {
      input.select();
      toast("Bitte manuell kopieren.");
    }
  });

  $("btnShareRevoke").addEventListener("click", async () => {
    if (!currentProjectId) return;
    const ok = await confirmDialog(
      "Freigabe beenden",
      "Der bisherige Link funktioniert danach nicht mehr.",
      { confirmLabel: "Beenden" }
    );
    if (!ok) return;
    try {
      await api.revokeShareLink(currentProjectId);
      getProject().shareToken = null;
      $("shareOff").hidden = false;
      $("shareOn").hidden = true;
    } catch (err) {
      toast(`Fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
    }
  });
}

let settingsRendered = false;
function wireTabs(): void {
  document.querySelectorAll<HTMLElement>(".tab").forEach((tab) => {
    tab.addEventListener("click", () => {
      document.querySelectorAll(".tab").forEach((x) => x.classList.remove("active"));
      document.querySelectorAll(".tab-panel").forEach((x) => x.classList.remove("active"));
      tab.classList.add("active");
      $(`panel-${tab.dataset.tab}`).classList.add("active");
      const s = session();
      if (tab.dataset.tab === "objekte" && s.mode === "shape") setMode("select");
      if (tab.dataset.tab === "flaechen" && s.mode === "place") setMode("select");
      if (tab.dataset.tab === "einstellungen") {
        renderSettingsInto($("settingsRoot"), currentUser.username);
        settingsRendered = true;
      }
    });
  });
  void settingsRendered;
}

async function onImportFile(ev: Event): Promise<void> {
  const input = ev.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file || !currentProjectId) return;
  try {
    const raw = JSON.parse(await file.text());
    const migrated = migrateImport(raw);
    const updated = await api.importState(currentProjectId, migrated);
    loadState(updated);
    renderAll();
    toast("Import erfolgreich.");
  } catch (err) {
    toast(`Import fehlgeschlagen: ${err instanceof Error ? err.message : "ungültige Datei"}`);
  }
}

async function onRename(): Promise<void> {
  if (!currentProjectId) return;
  const next = await textPrompt("Foyer umbenennen", getProject().name, {
    label: "Name",
    confirmLabel: "Umbenennen",
  });
  if (!next) return;
  try {
    const project = await api.patchProject(currentProjectId, { name: next });
    Object.assign(getProject(), project);
    updatePlannerHeader();
    toast("Umbenannt.");
  } catch (err) {
    toast(`Umbenennen fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
  }
}

async function onAssignBuilding(): Promise<void> {
  if (!currentProjectId) return;
  const id = currentProjectId;
  const p = getProject();
  const buildings = await api.listBuildings();

  const dlg = document.createElement("dialog");
  dlg.className = "dialog";
  dlg.innerHTML = `
    <form method="dialog">
      <h2>Gebäude &amp; Stockwerk</h2>
      <div class="field"><label>Gebäude</label><select data-f="b"></select></div>
      <div class="field"><label>Stockwerk</label><select data-f="f"></select></div>
      <div class="field-hint">Gebäude und Stockwerke werden in der Übersicht gepflegt.</div>
      <div class="dialog-actions">
        <button type="button" class="btn ghost" data-a="cancel">Abbrechen</button>
        <button type="submit" class="btn primary" data-a="ok">Speichern</button>
      </div>
    </form>`;
  document.body.appendChild(dlg);
  dlg.addEventListener("close", () => dlg.remove());

  const bSel = dlg.querySelector<HTMLSelectElement>('[data-f="b"]')!;
  const fSel = dlg.querySelector<HTMLSelectElement>('[data-f="f"]')!;
  bSel.appendChild(new Option("— kein Gebäude —", ""));
  for (const b of buildings) bSel.appendChild(new Option(b.name, b.id));
  bSel.value = p.buildingId ?? "";
  const fillFloors = () => {
    const b = buildings.find((x) => x.id === bSel.value);
    fSel.replaceChildren(new Option(b ? "— Stockwerk wählen —" : "— kein Stockwerk —", ""));
    for (const f of b?.floors ?? []) fSel.appendChild(new Option(f.name, f.id));
    fSel.disabled = !b;
  };
  fillFloors();
  fSel.value = p.floorId ?? "";
  bSel.addEventListener("change", fillFloors);

  dlg.querySelector('[data-a="cancel"]')!.addEventListener("click", () => dlg.close());
  dlg.querySelector("form")!.addEventListener("submit", async () => {
    try {
      const project = await api.patchProject(id, {
        buildingId: bSel.value || null,
        floorId: fSel.value || null,
      });
      Object.assign(getProject(), project);
      updatePlannerHeader();
      toast("Zuordnung gespeichert.");
    } catch (err) {
      toast(`Speichern fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
    }
    dlg.close();
  });
  dlg.showModal();
}

async function onDeleteProject(): Promise<void> {
  if (!currentProjectId) return;
  const ok = await confirmDialog(
    "Foyer löschen",
    `„${getProject().name}“ und alle darin platzierten Objekte, Flächen und Tensatoren werden dauerhaft gelöscht. Der Grundriss wird ebenfalls entfernt.`,
    { confirmLabel: "Endgültig löschen" }
  );
  if (!ok) return;
  try {
    await api.deleteProject(currentProjectId);
    navigateTo("/");
  } catch (err) {
    toast(`Löschen fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
  }
}

/* -------------------------------- boot -------------------------------- */

async function bootAuthed(user: AuthUser): Promise<void> {
  currentUser = user;
  showScreen("app");
  $("userMenuName").textContent = user.username;

  if (!bootedAuthed) {
    bootedAuthed = true;
    await loadConfig();

    initProjects(
      {
        screen: screenProjects,
        grid: $("projectGrid"),
        empty: $("projectEmpty"),
        btnNew: $("btnNewProject") as HTMLButtonElement,
        btnNewEmpty: $("btnNewProjectEmpty") as HTMLButtonElement,
        btnManageBuildings: $("btnManageBuildings") as HTMLButtonElement,
        dialog: $("newProjectDialog") as HTMLDialogElement,
        form: $("newProjectForm") as HTMLFormElement,
        name: $("npName") as HTMLInputElement,
        building: $("npBuilding") as HTMLSelectElement,
        floor: $("npFloor") as HTMLSelectElement,
        file: $("npFile") as HTMLInputElement,
        ratio: $("npRatio") as HTMLInputElement,
        fileHint: $("npFileHint"),
        previewWrap: $("npPreviewWrap"),
        previewCanvas: $("npPreviewCanvas") as HTMLCanvasElement,
        previewInfo: $("npPreviewInfo"),
        error: $("npError"),
        submit: $("npSubmit") as HTMLButtonElement,
        cancel: $("npCancel") as HTMLButtonElement,
      },
      { onOpen: (id) => navigateTo(`/p/${encodeURIComponent(id)}`) }
    );

    // overview header
    const toggleUserMenu = (e: Event) => {
      e.stopPropagation();
      $("userMenu").classList.toggle("open");
    };
    $("btnUserMenu").addEventListener("click", toggleUserMenu);
    $("userMenu").addEventListener("click", (e) => e.stopPropagation());
    document.addEventListener("click", () => $("userMenu").classList.remove("open"));
    $("btnLogout").addEventListener("click", async () => {
      await api.logout();
      location.reload();
    });
    $("btnOverviewSettings").addEventListener("click", () => openSettingsDialog(currentUser.username));

    window.addEventListener("popstate", () => void route());
  }

  await route();
}

async function main(): Promise<void> {
  initToast($("toastWrap"));

  // Public read-only share link — bypasses login entirely, no /api/auth call.
  const shareMatch = /^\/share\/([^/]+)\/?$/.exec(location.pathname);
  if (shareMatch) {
    try {
      await bootShareView(decodeURIComponent(shareMatch[1]!));
    } catch (err) {
      console.error("bootShareView failed:", err);
      document.body.innerHTML = `<pre style="padding:20px; white-space:pre-wrap;">${
        err instanceof Error ? `${err.message}\n${err.stack}` : String(err)
      }</pre>`;
    }
    return;
  }

  setUnauthorizedHandler(() => {
    if (bootedAuthed) location.reload();
    else showAuth(false);
  });

  try {
    const { user, needsSetup } = await api.me();
    if (user) await bootAuthed(user);
    else showAuth(needsSetup);
  } catch {
    showAuth(false);
  }
}

void main();
